import { useAuth } from "@clerk/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useState, type ReactElement, type ReactNode } from "react";
import type {
  AlwaysAiUser,
  ClientNotificationSettingsResponse,
  PatientPageAboutThem,
  PatientPageResponse,
} from "../../../shared/api-types";
import { FEATURE_FLAGS } from "../../../shared/features";
import {
  PatientQuickNoteBody,
  PatientQuickNoteReadOnly,
} from "@/components/appointments/patient-quick-note";
import { NotFound } from "@/components/not-found";
import { BreadcrumbSetter } from "@/components/v2/breadcrumb";
import {
  PatientList,
  PatientsHeading,
  type PatientLinkProps,
} from "@/components/v2/patient-list-parts";
import {
  PatientNotificationsCard,
  type NotificationsCardState,
} from "@/components/v2/patient-notifications-card";
import {
  AboutThemCard,
  AboutThemEmptyLine,
  AppointmentsCard,
  PATIENT_NOT_IN_LIST_MESSAGE,
  PATIENT_PAGE_UNAVAILABLE_MESSAGE,
  PatientPageColumns,
  PatientPageHeader,
  PatientPageMessage,
  type RenderBackLink,
  type RenderMessagesLink,
  type RenderNoteLink,
} from "@/components/v2/patient-page-parts";
import { PatientRedFlagSection } from "@/components/v2/patient-red-flag-section";
import { QueryErrorState } from "@/components/v2/query-error-state";
import { ListCard } from "@/components/v2/quick-facts-and-goals-cards";
import { useShellFrame } from "@/components/v2/shell-layout";
import { authenticatedApi } from "@/lib/api";
import { deviceTimezone } from "@/lib/appointments";
import {
  aboutThemUpdatedLabel,
  isQuickNoteEmpty,
  patientPageQueryKey,
  visibleAboutThem,
} from "@/lib/patient-page-client";
import { showsPatientMessagesLink } from "@/lib/messages-client";
import {
  patientListView,
  patientsPageState,
  type PatientsPageState,
} from "@/lib/patients-client";
import { can } from "@/lib/permissions";
import { listCardVisibility, type ListKind } from "@/lib/quick-facts-and-goals-client";
import {
  PATIENT_DETAIL_FLAG,
  PATIENT_MESSAGING_FLAG,
  useFeatureEnabled,
  useFeatures,
} from "@/lib/use-features";
import { usePatientNotificationsCard } from "@/lib/use-patient-notifications";
import {
  usePatientList,
  usePatientPage,
  useQuickFactsOrGoals,
  useSaveQuickFactsOrGoals,
} from "@/lib/use-patients";

// /patients and /patients/$patientId (NO-835): the RD's own Clients, in the
// single-rail frame only and behind `patient-detail`. Blank while the flags
// and the viewer load; the router's 404 for anyone the rail would not show
// the Patients row to. The Client page (NO-849) is the header, About them and
// Appointments from GET /api/patients/:patientId, in a wider two-column frame
// whose right column holds Quick facts and Goals (NO-852) and, when
// `notifications` is on too, NO-851's Notifications card.

const PAGE_FRAME = "mx-auto max-w-[880px] px-6 pb-12 pt-8";

const PATIENT_PAGE_FRAME = "mx-auto max-w-[1200px] px-6 pb-12 pt-8";

type PatientPageReady = Extract<PatientPageResponse, { status: "ok" }>;

function useCurrentUser() {
  const { getToken } = useAuth();

  return useQuery({
    queryKey: ["current-user"],
    queryFn: () => authenticatedApi<{ user: AlwaysAiUser }>(getToken, "/api/me"),
  });
}

function usePatientsPageState(): PatientsPageState {
  const frame = useShellFrame();
  const features = useFeatures();
  const me = useCurrentUser();
  const user = me.data?.user;

  return patientsPageState({
    frame,
    featuresLoading: features.isPending || me.isPending,
    patientDetailEnabled: features.data?.data[PATIENT_DETAIL_FLAG] === true,
    canReadOwnAppointments: user ? can(user, "appointments:read:own") : false,
  });
}

/** Renders the gate's answer, and `children` only once the page may show. */
function PatientsGate({ children }: { children: ReactNode }): ReactElement | null {
  const state = usePatientsPageState();

  if (state === "blank") return null;

  if (state === "not_found") return <NotFound />;

  return <>{children}</>;
}

export function PatientsPage(): ReactElement | null {
  return (
    <PatientsGate>
      <PatientsContent />
    </PatientsGate>
  );
}

export function PatientPage(): ReactElement | null {
  return (
    <PatientsGate>
      <PatientPageContent />
    </PatientsGate>
  );
}

function renderPatientLink({
  patientId,
  className,
  dataAttributes,
  children,
}: PatientLinkProps): ReactElement {
  return (
    <Link
      to="/patients/$patientId"
      params={{ patientId }}
      className={className}
      {...dataAttributes}
    >
      {children}
    </Link>
  );
}

function PatientsContent(): ReactElement {
  const list = usePatientList();
  const [now] = useState(() => new Date());

  return (
    <div className={PAGE_FRAME}>
      <BreadcrumbSetter segments={[{ label: "Patients" }]} />
      <PatientsHeading />
      {list.isError ? (
        <QueryErrorState
          error={list.error}
          notFoundText="Patients are not available"
          onRetry={() => void list.refetch()}
          className="mt-10"
        />
      ) : list.data ? (
        <PatientList
          view={patientListView(list.data)}
          timezone={deviceTimezone()}
          now={now}
          renderPatientLink={renderPatientLink}
        />
      ) : null}
    </div>
  );
}

function renderBackLink({
  className,
  children,
}: Parameters<RenderBackLink>[0]): ReactElement {
  return (
    <Link to="/patients" className={className}>
      {children}
    </Link>
  );
}

function renderMessagesLink({
  patientId,
  className,
  children,
}: Parameters<RenderMessagesLink>[0] & { patientId: string }): ReactElement {
  return (
    <Link to="/messages/$patientId" params={{ patientId }} className={className}>
      {children}
    </Link>
  );
}

function renderNoteLink({
  appointmentId,
  className,
  ariaLabel,
  children,
}: Parameters<RenderNoteLink>[0]): ReactElement {
  return (
    <Link
      to="/appointments/$appointmentId"
      params={{ appointmentId }}
      search={{}}
      className={className}
      aria-label={ariaLabel}
    >
      {children}
    </Link>
  );
}

function PatientPageContent(): ReactElement {
  const { patientId } = useParams({ from: "/workspace/patients/$patientId" });
  const page = usePatientPage(patientId);
  const [now] = useState(() => new Date());

  return (
    <div className={PATIENT_PAGE_FRAME}>
      <BreadcrumbSetter segments={[{ label: "Patients" }]} />
      {page.isError ? (
        <PatientPageMessage renderBackLink={renderBackLink}>
          <QueryErrorState
            error={page.error}
            notFoundText={PATIENT_NOT_IN_LIST_MESSAGE}
            onRetry={() => void page.refetch()}
            className="mt-6"
          />
        </PatientPageMessage>
      ) : page.data ? (
        <PatientPageBody patientId={patientId} response={page.data} now={now} />
      ) : null}
    </div>
  );
}

function PatientPageBody({
  patientId,
  response,
  now,
}: {
  patientId: string;
  response: PatientPageResponse;
  now: Date;
}): ReactElement {
  if (response.status === "unavailable") {
    return (
      <PatientPageMessage renderBackLink={renderBackLink}>
        {PATIENT_PAGE_UNAVAILABLE_MESSAGE}
      </PatientPageMessage>
    );
  }

  if (response.status === "no_selection") {
    return (
      <PatientPageMessage renderBackLink={renderBackLink}>
        {PATIENT_NOT_IN_LIST_MESSAGE}
      </PatientPageMessage>
    );
  }

  return <PatientPageSections patientId={patientId} response={response} now={now} />;
}

/** The patient page's Messages link: both flags and the RD's own rows (NO-925). */
function useShowsMessagesLink(viewer: AlwaysAiUser | undefined): boolean {
  return showsPatientMessagesLink({
    patientDetailEnabled: useFeatureEnabled(PATIENT_DETAIL_FLAG),
    messagingEnabled: useFeatureEnabled(PATIENT_MESSAGING_FLAG),
    canReadOwnAppointments: viewer ? can(viewer, "appointments:read:own") : false,
  });
}

function PatientPageSections({
  patientId,
  response,
  now,
}: {
  patientId: string;
  response: PatientPageReady;
  now: Date;
}): ReactElement {
  const timezone = deviceTimezone();
  const quickNoteEnabled = useFeatureEnabled(FEATURE_FLAGS.patientQuickNote);
  const aboutThem = visibleAboutThem(response.aboutThem, quickNoteEnabled);
  const notificationsShown = useFeatureEnabled(FEATURE_FLAGS.notifications);
  const viewer = useCurrentUser().data?.user;
  const messagesLink = useShowsMessagesLink(viewer);

  return (
    <>
      <PatientPageHeader
        patientId={patientId}
        header={response.header}
        timezone={timezone}
        now={now}
        renderBackLink={renderBackLink}
        renderMessagesLink={
          messagesLink
            ? (props) => renderMessagesLink({ ...props, patientId })
            : undefined
        }
      />
      <PatientRedFlagSection patientId={patientId} viewer={viewer} />
      <PatientPageColumns
        aside={
          <>
            <QuickFactsAndGoals patientId={patientId} />
            {notificationsShown ? (
              <PatientNotificationsSection patientId={patientId} />
            ) : null}
          </>
        }
      >
        {aboutThem ? (
          <AboutThemSection
            patientId={patientId}
            aboutThem={aboutThem}
            timezone={timezone}
            now={now}
          />
        ) : null}
        <AppointmentsCard
          appointments={response.appointments}
          timezone={timezone}
          now={now}
          renderNoteLink={renderNoteLink}
        />
      </PatientPageColumns>
    </>
  );
}

/**
 * The Notifications card (NO-851). Mounted only with `notifications` on, so a
 * viewer the flag hides never reads the Client's choices.
 */
function PatientNotificationsSection({
  patientId,
}: {
  patientId: string;
}): ReactElement {
  const card = usePatientNotificationsCard(patientId);
  const { query } = card;
  const view = query.data;

  return (
    <PatientNotificationsCard
      state={notificationsCardState(query.isError, view)}
      draft={card.editor?.draft ?? null}
      notice={card.notice}
      saving={card.saving}
      onRetry={() => void query.refetch()}
      onEdit={() => {
        if (view) card.edit(view);
      }}
      onDraft={card.draftRow}
      onSave={card.submit}
      onCancel={card.cancel}
    />
  );
}

function notificationsCardState(
  failed: boolean,
  view: ClientNotificationSettingsResponse | undefined
): NotificationsCardState {
  if (failed) return { kind: "failed" };

  return view ? { kind: "ready", view } : { kind: "loading" };
}

/**
 * About them reads the note; Edit (with patient_quick_notes:write:own) swaps
 * in the Quick Note editor on the Client-keyed routes. Done reloads the page
 * so the note and its Updated-after label come from the server.
 */
function AboutThemSection({
  patientId,
  aboutThem,
  timezone,
  now,
}: {
  patientId: string;
  aboutThem: PatientPageAboutThem;
  timezone: string;
  now: Date;
}): ReactElement {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const user = useCurrentUser().data?.user;
  const canEdit = user ? can(user, "patient_quick_notes:write:own") : false;
  const [editing, setEditing] = useState(false);
  const showsEdit = canEdit && !editing;

  function finishEditing(): void {
    setEditing(false);
    void queryClient.invalidateQueries({ queryKey: patientPageQueryKey(patientId) });
  }

  return (
    <AboutThemCard
      updatedLabel={aboutThemUpdatedLabel(aboutThem, timezone, now)}
      onEdit={showsEdit ? () => setEditing(true) : undefined}
    >
      {editing ? (
        <PatientQuickNoteBody
          subject={{ patientId }}
          getToken={getToken}
          onDone={finishEditing}
        />
      ) : (
        <AboutThemReadView aboutThem={aboutThem} />
      )}
    </AboutThemCard>
  );
}

function AboutThemReadView({
  aboutThem,
}: {
  aboutThem: PatientPageAboutThem;
}): ReactElement {
  if (isQuickNoteEmpty(aboutThem)) return <AboutThemEmptyLine />;

  return (
    <PatientQuickNoteReadOnly
      key={aboutThem.revision?.id}
      document={aboutThem.document}
    />
  );
}

/**
 * NO-852: the right column's Quick facts and Goals. Each card loads and saves
 * on its own, behind the Quick Note's own-lane Permissions. A card shows
 * nothing without patient_quick_notes:read:own, while it loads, or when its
 * read answers 403 or 404 (not this Client's RD); any other failure is a
 * neutral line with Try again inside that card.
 */
function QuickFactsAndGoals({ patientId }: { patientId: string }): ReactElement {
  const user = useCurrentUser().data?.user;
  const canRead = user ? can(user, "patient_quick_notes:read:own") : false;
  const canEdit = user ? can(user, "patient_quick_notes:write:own") : false;

  return (
    <>
      <ListCardSection
        kind="quick_facts"
        patientId={patientId}
        canRead={canRead}
        canEdit={canEdit}
      />
      <ListCardSection
        kind="goals"
        patientId={patientId}
        canRead={canRead}
        canEdit={canEdit}
      />
    </>
  );
}

function ListCardSection({
  kind,
  patientId,
  canRead,
  canEdit,
}: {
  kind: ListKind;
  patientId: string;
  canRead: boolean;
  canEdit: boolean;
}): ReactElement | null {
  const list = useQuickFactsOrGoals(kind, patientId, canRead);
  const save = useSaveQuickFactsOrGoals(kind, patientId);
  const shown = listCardVisibility({
    canRead,
    isError: list.isError,
    error: list.error,
    data: list.data,
  });

  if (shown.kind === "hidden") return null;

  const load =
    shown.kind === "retry"
      ? { status: "failed" as const, onRetry: () => void list.refetch() }
      : { status: "ready" as const, list: shown.list };

  return <ListCard kind={kind} load={load} canEdit={canEdit} onSave={save} />;
}
